'use client';

import React, { useRef, useEffect } from 'react';
import Hls from 'hls.js';
import { useTranslation } from 'react-i18next';
import { parseNowrap } from '@/lib/utils/utils';
import { breakpoints } from '@/app/config/breakpoints';
import gsap from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';
import useViewportActivation from '@/app/lib/hooks/use-viewport-activation';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const TABLET_MEDIA_QUERY = `(min-width: ${breakpoints.tablet}px)`;
const TABLET_STICKY_SCROLL_UNITS = 3.1;

const HLS_SOURCES = [
    `${process.env.NEXT_PUBLIC_VIDEO_BASE_URL}/home/protect-privacy-video-1-hls/index.m3u8`,
    `${process.env.NEXT_PUBLIC_VIDEO_BASE_URL}/home/protect-privacy-video-2-hls/index.m3u8`,
    `${process.env.NEXT_PUBLIC_VIDEO_BASE_URL}/home/protect-privacy-video-3-hls/index.m3u8`,
    `${process.env.NEXT_PUBLIC_VIDEO_BASE_URL}/home/protect-privacy-video-4-hls/index.m3u8`,
];

const VIDEO_POSTERS = [
    getStaticAssetUrl('/home/img-protect-privacy-video-1-cover.png'),
    getStaticAssetUrl('/home/img-protect-privacy-video-2-cover.png'),
    getStaticAssetUrl('/home/img-protect-privacy-video-3-cover.png'),
    getStaticAssetUrl('/home/img-protect-privacy-video-4-cover.png'),
];

const getVideoPosterStyle = (poster: string): React.CSSProperties => ({
    backgroundImage: `url(${poster})`,
    backgroundPosition: 'right bottom',
    backgroundSize: 'cover',
});

const clampVideoIndex = (index: number) =>
    Math.min(HLS_SOURCES.length - 1, Math.max(0, index));

const HlsVideoPlayer = ({
    src,
    poster,
    isActive = true,
    restartOnActive = false,
    enabled = true,
    className,
}: {
    src: string;
    poster: string;
    isActive?: boolean;
    restartOnActive?: boolean;
    enabled?: boolean;
    className?: string;
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const isActiveRef = useRef(isActive);

    useEffect(() => {
        isActiveRef.current = isActive;
    }, [isActive]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        let hls: Hls | null = null;

        if (!enabled) {
            video.pause();
            video.removeAttribute('src');
            video.load();
            return;
        }

        const playVideo = () => {
            if (!isActiveRef.current) return;
            video.play().catch((error: unknown) => {
                if (
                    error instanceof DOMException &&
                    error.name === 'AbortError'
                ) {
                    return;
                }

                console.error(error);
            });
        };

        if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = src;
            video.addEventListener('loadedmetadata', playVideo);
        } else if (Hls.isSupported()) {
            hls = new Hls();
            hls.loadSource(src);
            hls.attachMedia(video);
            hls.on(Hls.Events.MANIFEST_PARSED, playVideo);
        }

        return () => {
            video.removeEventListener('loadedmetadata', playVideo);
            video.pause();
            video.removeAttribute('src');
            video.load();
            if (hls) hls.destroy();
        };
    }, [src, enabled]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        if (!enabled || !isActive) {
            video.pause();
            return;
        }

        const restartAndPlay = () => {
            if (restartOnActive) {
                video.currentTime = 0;
            }

            video.play().catch((error: unknown) => {
                if (
                    error instanceof DOMException &&
                    error.name === 'AbortError'
                ) {
                    return;
                }

                console.error(error);
            });
        };

        if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
            restartAndPlay();
        } else {
            video.addEventListener('loadedmetadata', restartAndPlay, {
                once: true,
            });
        }

        return () => {
            video.removeEventListener('loadedmetadata', restartAndPlay);
        };
    }, [enabled, isActive, restartOnActive]);

    return (
        <video
            ref={videoRef}
            playsInline
            autoPlay={isActive}
            muted
            loop
            poster={poster}
            className={className}
            onContextMenu={(e) => e.preventDefault()}
        />
    );
};

type ActiveLayout = 'tablet' | 'mobile';

interface MobileVideoItemProps {
    title: string;
    description: string;
    src: string;
    poster: string;
}

function MobileVideoItem({
    title,
    description,
    src,
    poster,
}: Readonly<MobileVideoItemProps>) {
    const { ref, isActive } = useViewportActivation<HTMLDivElement>({
        rootMargin: '250px 0px',
    });

    return (
        <div ref={ref} className="flex w-full flex-col gap-[16px]">
            <div className="flex flex-col">
                <p className="j-h6 text-white">{title}</p>
                <p className="j-t2 text-[#CCCBCE]">{description}</p>
            </div>
            <div
                className="aspect-[760/432] w-full overflow-hidden rounded-[4px] bg-[#1A1A1A]"
                style={getVideoPosterStyle(poster)}
            >
                <HlsVideoPlayer
                    src={src}
                    poster={poster}
                    enabled={isActive}
                    isActive={isActive}
                    className="h-full w-full object-cover object-right-bottom"
                />
            </div>
        </div>
    );
}

const useActiveLayout = () => {
    const [activeLayout, setActiveLayout] = React.useState<ActiveLayout | null>(
        null,
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia(TABLET_MEDIA_QUERY);
        const updateLayout = () =>
            setActiveLayout(mediaQuery.matches ? 'tablet' : 'mobile');

        updateLayout();

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', updateLayout);
        } else {
            mediaQuery.addListener(updateLayout);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', updateLayout);
            } else {
                mediaQuery.removeListener(updateLayout);
            }
        };
    }, []);

    return activeLayout;
};

// 渐变文字 class
const GRADIENT_TEXT =
    'bg-[linear-gradient(90deg,#7A58FF_0%,#E6A8FD_100%)] bg-clip-text [-webkit-background-clip:text] [-webkit-text-fill-color:transparent] text-transparent';

export default function ProtectPrivacy() {
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [loadedTabletIndices, setLoadedTabletIndices] = React.useState(
        () => new Set<number>([0]),
    );
    const activeLayout = useActiveLayout();
    const { t } = useTranslation('protect-privacy');

    // outerRef: 高容器，提供滚动空间；CSS sticky 负责吸附
    const outerRef = useRef<HTMLDivElement>(null);
    const activeIndexRef = useRef(0);
    const scrollTweenRef = useRef<gsap.core.Tween | null>(null);
    const isProgrammaticScrollRef = useRef(false);
    const unlockScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const wheelAccumulatorRef = useRef(0);
    const { ref: activationRef, isActive: shouldLoadVideos } =
        useViewportActivation<HTMLDivElement>({
            rootMargin: '400px 0px',
        });

    const setOuterRefs = (node: HTMLDivElement | null) => {
        outerRef.current = node;
        activationRef.current = node;
    };

    const resetWheelAccumulator = () => {
        wheelAccumulatorRef.current = 0;
    };

    const isStickyActive = () => {
        const outer = outerRef.current;
        if (!outer) {
            return false;
        }

        const rect = outer.getBoundingClientRect();

        return rect.top <= 0 && rect.bottom >= window.innerHeight;
    };

    const isTabletLayout = () =>
        activeLayout === 'tablet' ||
        (activeLayout === null &&
            window.matchMedia(TABLET_MEDIA_QUERY).matches);

    const syncActiveIndex = (index: number) => {
        const nextIndex = clampVideoIndex(index);

        activeIndexRef.current = nextIndex;
        setActiveIndex((currentIndex) =>
            currentIndex === nextIndex ? currentIndex : nextIndex,
        );
    };

    const getScrollTopForIndex = (index: number) => {
        const outer = outerRef.current;
        if (!outer) {
            return null;
        }

        const scrollableDistance = Math.max(
            0,
            outer.offsetHeight - window.innerHeight,
        );
        const targetProgress =
            HLS_SOURCES.length > 1
                ? clampVideoIndex(index) / (HLS_SOURCES.length - 1)
                : 0;

        return (
            outer.getBoundingClientRect().top +
            window.scrollY +
            scrollableDistance * targetProgress
        );
    };

    const scrollToIndex = (index: number, duration: number) => {
        const nextIndex = clampVideoIndex(index);
        const targetScrollTop = getScrollTopForIndex(nextIndex);

        if (targetScrollTop === null) {
            syncActiveIndex(nextIndex);
            return;
        }

        scrollTweenRef.current?.kill();
        if (unlockScrollTimeoutRef.current) {
            clearTimeout(unlockScrollTimeoutRef.current);
            unlockScrollTimeoutRef.current = null;
        }

        isProgrammaticScrollRef.current = true;
        syncActiveIndex(nextIndex);

        scrollTweenRef.current = gsap.to(window, {
            duration,
            ease: 'power2.out',
            overwrite: 'auto',
            scrollTo: {
                y: targetScrollTop,
                autoKill: false,
            },
            onUpdate: () => {
                ScrollTrigger.update();
            },
            onComplete: () => {
                scrollTweenRef.current = null;
                syncActiveIndex(nextIndex);
                ScrollTrigger.update();
                unlockScrollTimeoutRef.current = setTimeout(() => {
                    isProgrammaticScrollRef.current = false;
                    unlockScrollTimeoutRef.current = null;
                }, 120);
            },
            onInterrupt: () => {
                isProgrammaticScrollRef.current = false;
                scrollTweenRef.current = null;
                if (unlockScrollTimeoutRef.current) {
                    clearTimeout(unlockScrollTimeoutRef.current);
                    unlockScrollTimeoutRef.current = null;
                }
                ScrollTrigger.update();
            },
        });
    };

    useEffect(() => {
        const mm = gsap.matchMedia();

        mm.add(TABLET_MEDIA_QUERY, () => {
            // CSS sticky handles pinning. ScrollTrigger only normalizes boundary state;
            // active item changes are discrete via click/wheel snap, not continuous scrub.
            const trigger = ScrollTrigger.create({
                trigger: outerRef.current,
                start: 'top top',
                end: 'bottom bottom',
                onLeave: () => {
                    if (isProgrammaticScrollRef.current) {
                        return;
                    }

                    resetWheelAccumulator();
                    syncActiveIndex(HLS_SOURCES.length - 1);
                },
                onLeaveBack: () => {
                    if (isProgrammaticScrollRef.current) {
                        return;
                    }

                    resetWheelAccumulator();
                    syncActiveIndex(0);
                },
            });

            return () => {
                trigger.kill();
            };
        });

        return () => {
            scrollTweenRef.current?.kill();
            isProgrammaticScrollRef.current = false;
            if (unlockScrollTimeoutRef.current) {
                clearTimeout(unlockScrollTimeoutRef.current);
                unlockScrollTimeoutRef.current = null;
            }
            scrollTweenRef.current = null;
            mm.revert();
        };
    }, []);

    const handleSelect = (index: number) => {
        wheelAccumulatorRef.current = 0;

        if (isTabletLayout()) {
            scrollToIndex(index, 0.45);
        } else {
            syncActiveIndex(index);
        }
    };

    useEffect(() => {
        if (activeLayout === 'tablet') {
            return;
        }

        wheelAccumulatorRef.current = 0;
    }, [activeLayout]);

    const handleTabletWheel = (event: React.WheelEvent<HTMLDivElement>) => {
        if (!isTabletLayout() || !isStickyActive()) {
            return;
        }

        const direction = event.deltaY > 0 ? 1 : event.deltaY < 0 ? -1 : 0;
        const lastIndex = HLS_SOURCES.length - 1;
        const currentIndex = activeIndexRef.current;
        const isAtBoundary =
            (direction > 0 && currentIndex >= lastIndex) ||
            (direction < 0 && currentIndex <= 0);

        if (direction === 0) {
            return;
        }

        if (isProgrammaticScrollRef.current) {
            event.preventDefault();
            return;
        }

        if (isAtBoundary) {
            wheelAccumulatorRef.current = 0;
            return;
        }

        event.preventDefault();
        wheelAccumulatorRef.current += event.deltaY;
        const WHEEL_STEP_THRESHOLD = 48;
        let stepCount = 0;

        while (wheelAccumulatorRef.current >= WHEEL_STEP_THRESHOLD) {
            wheelAccumulatorRef.current -= WHEEL_STEP_THRESHOLD;
            stepCount += 1;
        }

        while (wheelAccumulatorRef.current <= -WHEEL_STEP_THRESHOLD) {
            wheelAccumulatorRef.current += WHEEL_STEP_THRESHOLD;
            stepCount -= 1;
        }

        if (stepCount === 0) {
            return;
        }

        const nextIndex = Math.min(
            lastIndex,
            Math.max(0, currentIndex + Math.sign(stepCount)),
        );

        if (nextIndex === currentIndex) {
            return;
        }

        wheelAccumulatorRef.current = 0;
        scrollToIndex(nextIndex, 0.28);
    };

    useEffect(() => {
        if (activeLayout !== 'tablet' || !shouldLoadVideos) {
            setLoadedTabletIndices(new Set([activeIndex]));
            return;
        }

        setLoadedTabletIndices((previous) => {
            if (previous.has(activeIndex)) {
                return previous;
            }

            const next = new Set(previous);
            next.add(activeIndex);
            return next;
        });
    }, [activeIndex, activeLayout, shouldLoadVideos]);

    const titleBlock = (
        <div className="tablet:text-center j-h4 tablet:j-h3 text-start tracking-wide">
            <div className="text-white">{t('title.part1')}</div>
            <div className="text-white">{t('title.part2')}</div>
            <div className={GRADIENT_TEXT}>{parseNowrap(t('title.part3'))}</div>
            <div className={GRADIENT_TEXT}>{t('title.part4')}</div>
        </div>
    );

    return (
        <>
            {/* ── Tablet+ Layout：外层高容器提供滚动空间，内层 sticky 吸附 ── */}
            <div
                ref={setOuterRefs}
                className="tablet:block hidden bg-black"
                style={{
                    height: `calc(var(--homepage-vh, 100svh) * ${TABLET_STICKY_SCROLL_UNITS})`,
                }}
                onWheel={handleTabletWheel}
            >
                {/* sticky 内容区：在外层滚动时保持固定，直到外层底部离开视口 */}
                <div
                    className="sticky top-0 flex w-full flex-col items-center justify-center"
                    style={{ height: 'var(--homepage-vh, 100svh)' }}
                >
                    <div className="flex w-[87.5vw] max-w-[1160px] flex-col items-center gap-[48px]">
                        {titleBlock}

                        {/* Desktop / Tablet 内容 */}
                        <div className="flex w-full flex-row items-stretch gap-[40px]">
                            {/* Left text column */}
                            <div className="desktop:w-[320px] flex w-[300px] shrink-0 flex-col justify-center text-left">
                                {/* Item 1 */}
                                <div
                                    className="desktop:pb-[24px] flex cursor-pointer flex-col gap-[4px] border-b border-[#333333] pb-[16px]"
                                    onClick={() => handleSelect(0)}
                                >
                                    <p
                                        className={`j-h6 ${activeIndex === 0 ? GRADIENT_TEXT : 'text-[#E5E5E5] transition-colors'}`}
                                    >
                                        {t('leftData.part1.title')}
                                    </p>
                                    <p
                                        className={`j-t2 ${activeIndex === 0 ? GRADIENT_TEXT : 'text-[#808080] transition-colors'}`}
                                    >
                                        {t('leftData.part1.desc')}
                                    </p>
                                </div>
                                {/* Item 2 */}
                                <div
                                    className="desktop:py-[24px] flex cursor-pointer flex-col gap-[4px] border-b border-[#333333] py-[16px]"
                                    onClick={() => handleSelect(1)}
                                >
                                    <p
                                        className={`j-h6 ${activeIndex === 1 ? GRADIENT_TEXT : 'text-[#E5E5E5] transition-colors'}`}
                                    >
                                        {t('leftData.part2.title')}
                                    </p>
                                    <p
                                        className={`j-t2 ${activeIndex === 1 ? GRADIENT_TEXT : 'text-[#808080] transition-colors'}`}
                                    >
                                        {t('leftData.part2.desc')}
                                    </p>
                                </div>
                                {/* Item 3 */}
                                <div
                                    className="desktop:py-[24px] flex cursor-pointer flex-col gap-[4px] border-b border-[#333333] py-[16px]"
                                    onClick={() => handleSelect(2)}
                                >
                                    <p
                                        className={`j-h6 ${activeIndex === 2 ? GRADIENT_TEXT : 'text-[#E5E5E5] transition-colors'}`}
                                    >
                                        {t('leftData.part3.title')}
                                    </p>
                                    <p
                                        className={`j-t2 ${activeIndex === 2 ? GRADIENT_TEXT : 'text-[#808080] transition-colors'}`}
                                    >
                                        {t('leftData.part3.desc')}
                                    </p>
                                </div>
                                {/* Item 4 */}
                                <div
                                    className="desktop:pt-[24px] flex cursor-pointer flex-col gap-[4px] pt-[16px]"
                                    onClick={() => handleSelect(3)}
                                >
                                    <p
                                        className={`j-h6 ${activeIndex === 3 ? GRADIENT_TEXT : 'text-[#E5E5E5] transition-colors'}`}
                                    >
                                        {t('leftData.part4.title')}
                                    </p>
                                    <p
                                        className={`j-t2 ${activeIndex === 3 ? GRADIENT_TEXT : 'text-[#808080] transition-colors'}`}
                                    >
                                        {t('leftData.part4.desc')}
                                    </p>
                                </div>
                            </div>

                            {/* Right media block - 预渲染全部视频，opacity 淡入淡出 */}
                            <div className="relative flex w-full flex-1 items-center justify-center">
                                {activeLayout === 'tablet' && (
                                    <div className="relative aspect-[760/432] w-full overflow-hidden rounded-[4px]">
                                        {HLS_SOURCES.map((src, i) => (
                                            <div
                                                key={src}
                                                className="absolute inset-0 transition-opacity duration-500 ease-out"
                                                style={{
                                                    ...getVideoPosterStyle(
                                                        VIDEO_POSTERS[i],
                                                    ),
                                                    opacity:
                                                        activeIndex === i
                                                            ? 1
                                                            : 0,
                                                    pointerEvents:
                                                        activeIndex === i
                                                            ? 'auto'
                                                            : 'none',
                                                }}
                                            >
                                                <HlsVideoPlayer
                                                    src={src}
                                                    poster={VIDEO_POSTERS[i]}
                                                    isActive={
                                                        shouldLoadVideos &&
                                                        activeIndex === i
                                                    }
                                                    restartOnActive
                                                    enabled={
                                                        shouldLoadVideos &&
                                                        loadedTabletIndices.has(
                                                            i,
                                                        )
                                                    }
                                                    className="h-full w-full object-cover object-right-bottom"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Mobile Layout：静态展示所有 item ── */}
            <section className="tablet:hidden flex w-full flex-col items-center bg-black py-[80px]">
                <div className="flex w-[85.5vw] max-w-[480px] flex-col items-center gap-[32px]">
                    {titleBlock}
                    <div className="flex w-full flex-col gap-12 text-left">
                        <MobileVideoItem
                            title={t('leftData.part1.title')}
                            description={t('leftData.part1.desc')}
                            src={HLS_SOURCES[0]}
                            poster={VIDEO_POSTERS[0]}
                        />
                        <MobileVideoItem
                            title={t('leftData.part2.title')}
                            description={t('leftData.part2.desc')}
                            src={HLS_SOURCES[1]}
                            poster={VIDEO_POSTERS[1]}
                        />
                        <MobileVideoItem
                            title={t('leftData.part3.title')}
                            description={t('leftData.part3.desc')}
                            src={HLS_SOURCES[2]}
                            poster={VIDEO_POSTERS[2]}
                        />
                        <MobileVideoItem
                            title={t('leftData.part4.title')}
                            description={t('leftData.part4.desc')}
                            src={HLS_SOURCES[3]}
                            poster={VIDEO_POSTERS[3]}
                        />
                    </div>
                </div>
            </section>
        </>
    );
}
